import type { PresenceMessage } from "ably";
import { useChannel, useConnectionStateListener } from "ably/react";
import { useCallback, useEffect, useRef, useState } from "react";
import type { PresenceData, PresenceState } from "../core";
import { ABLY_CONFIG, getRuntimeConfig } from "../core";
import type { ClientType } from "../vision";

export function usePresence(channelName: string, options?: { shouldManagePresence?: boolean }) {
	const shouldManagePresence = options?.shouldManagePresence ?? false;
	const [presenceState, setPresenceState] = useState<PresenceState>({
		memberIds: [],
		memberData: {},
		isInitialCheckComplete: false,
	});

	const { channel } = useChannel({ channelName });
	const mountedRef = useRef(true);

	useEffect(() => {
		mountedRef.current = true;
		return () => {
			mountedRef.current = false;
		};
	}, []);

	const enterPresence = useCallback(async () => {
		if (!channel || !shouldManagePresence) return;
		const logger = getRuntimeConfig().logger;
		const data: PresenceData = { screenVisible: document.visibilityState === "visible" };
		try {
			await channel.presence.enter(data);
			logger.info("FLOW", "Entered presence successfully", data);
		} catch (err) {
			logger.warning("INFO", "Failed to enter presence", err);
			setTimeout(async () => {
				if (!mountedRef.current) return;
				try {
					await channel.presence.enter(data);
					logger.info("FLOW", "Entered presence on retry", data);
				} catch (retryErr) {
					logger.warning("INFO", "Failed to enter presence on retry", retryErr);
				}
			}, ABLY_CONFIG.presenceRetryDelayMs);
		}
	}, [channel, shouldManagePresence]);

	useConnectionStateListener("connected", (stateChange) => {
		if (shouldManagePresence && (stateChange.previous === "disconnected" || stateChange.previous === "suspended")) {
			const logger = getRuntimeConfig().logger;
			logger.info("FLOW", "Re-entering presence after reconnection", { previous: stateChange.previous });
			enterPresence();
		}
	});

	useEffect(() => {
		if (!channel || !shouldManagePresence) return;
		const logger = getRuntimeConfig().logger;

		const handleVisibilityChange = () => {
			const screenVisible = document.visibilityState === "visible";
			logger.info("FLOW", "Screen visibility changed", { screenVisible });
			channel.presence.update({ screenVisible } satisfies PresenceData).catch((err) => {
				logger.warning("INFO", "Failed to update presence with screen visibility", err);
			});
		};

		document.addEventListener("visibilitychange", handleVisibilityChange);
		return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
	}, [channel, shouldManagePresence]);

	useEffect(() => {
		if (!channel || !channelName) return;
		const logger = getRuntimeConfig().logger;

		if (shouldManagePresence) {
			enterPresence();
		}

		const checkPresence = async () => {
			try {
				const members = await channel.presence.get();
				const memberIds = (
					members?.map((member: PresenceMessage) => member.clientId).filter(Boolean) || []
				).sort() as ClientType[];
				const memberData: Partial<Record<string, PresenceData>> = {};
				members?.forEach((member: PresenceMessage) => {
					if (member.clientId) {
						memberData[member.clientId] = (member.data as PresenceData) ?? {};
					}
				});

				setPresenceState((prev) => {
					const hasSameMembers =
						prev.memberIds.length === memberIds.length &&
						prev.memberIds.every((memberId, index) => memberId === memberIds[index]);
					const hasSameData = memberIds.every(
						(id) => prev.memberData[id]?.screenVisible === memberData[id]?.screenVisible,
					);
					if (!hasSameMembers) {
						logger.info("INFO", "Presence members changed", {
							presence_members: memberIds,
							previous_members: prev.memberIds,
						});
					}
					if (!hasSameData) {
						logger.info("INFO", "Presence data changed", { memberData });
					}
					if (prev.isInitialCheckComplete && hasSameMembers && hasSameData) {
						return prev;
					}
					return {
						memberIds,
						memberData,
						isInitialCheckComplete: true,
					};
				});
			} catch (err) {
				logger.warning("INFO", "Failed to get presence", err);
			}
		};

		channel.presence.subscribe(checkPresence);
		void checkPresence();

		return () => {
			channel.presence.unsubscribe(checkPresence);
			if (shouldManagePresence) {
				channel.presence.leave().catch(() => {});
			}
		};
	}, [channelName, channel, shouldManagePresence, enterPresence]);

	return presenceState;
}
